import cloneDeep from 'lodash/cloneDeep';
import includes from 'lodash/includes';
import {EventEmitter} from 'events';
import tsDate from '@techsee/techsee-date';
import {
    ACCENT_CLASS,
    RTL_LANGUAGES,
    LOG_REPETITION_TYPE,
    LOG_MESSAGES,
    CONNECTION_STATUS,
    RECONNECTION_SETTINGS
} from './ts-locale-helper.settings.js';
import {
    BrowserUtilsService,
    IBrowserUtilsService
} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {getRootStore} from '../../app.bootstrap';
import {DASHBOARD_LANGUAGE_KEY} from '../../states/helper/general.helper';
import STATUS_MESSAGES from '@techsee/techsee-common/lib/constants/status-messages.constant';

interface LogMessage {
    name: string;
    id: string;
    time: string | Date;
    isError?: boolean;
}

interface MessageDescription {
    group: number;
    repetition: string | number;
    message: string;
    accent: number;
    endsMeeting?: boolean;
    time?: string | Date;
    text?: string;
    isNew?: boolean;
}

interface PreparedMessage {
    order: number;
    group: number;
    text: string;
    accent: number;
    time: string;
    index?: number;
}

export interface ITsLocaleHelperService {
    // Event emitter methods (inherited)
    emit(event: string, ...args: any[]): boolean;
    on(event: string, listener: (...args: any[]) => void): this;

    // Main public methods
    handleMessage(message: LogMessage, sender: string, isNew: boolean): void;
    updateLogs(): void;
    mobileClientConnected(): void;
    mobileClientDisconnected(): void;
    eventIsError(event: PreparedMessage): boolean;
    eventIsWarning(event: PreparedMessage): boolean;
    loadLocale(locale?: string, companyName?: string): void;
    getLocale(): string;
    isRTL(): boolean;
    clear(): void;

    // Additional methods
    mobileClientHidden(): void;
    mobileClientVisible(): void;
    mobileNetworkCell(): void;
    mobileNetworkWifi(): void;
    dashboardConnectedOffline(): void;
    objectAnalyzingSuccess(): void;
    objectAnalyzingError(): void;
    objectAnalyzingStartTracking(): void;
    deviceAnalyzingError(): void;
}

/**
 * 1. Some common methods for language service
 * 2. Event Logs Dispatcher
 */
export class TsLocaleHelperService extends EventEmitter implements ITsLocaleHelperService {
    private ACCENT_CLASS: typeof ACCENT_CLASS;
    private browserUtilsService: IBrowserUtilsService;
    private _messages: PreparedMessage[] = [];
    private _messageCounter: number = 0;
    private _updateCounter: number = 0;
    private _registry: Record<string, Record<string, boolean>> = {};
    private _disconnectionTimer: number | null = null;
    private _meetingIsEndedByClient: boolean = false;
    private _tosRejected: boolean = false;
    public connected: boolean = false;

    constructor(browserUtilsService: IBrowserUtilsService) {
        super();

        this.browserUtilsService = browserUtilsService;
        this.ACCENT_CLASS = ACCENT_CLASS;

        this.clear();
    }

    // Override function for EventEmitter.emit - no Angular digest cycle needed
    emit(...args: any[]): boolean {
        // @ts-ignore
        return super.emit(...args);
    }

    /**
     * Handles Log Message updating this.logs array to show on event.logs screen
     */
    handleMessage(message: LogMessage, sender: string, isNew: boolean): void {
        const name = message.name;
        const logMessages = LOG_MESSAGES(getRootStore().localizationService.translate);
        const description = logMessages[name as keyof typeof logMessages];

        if (!description) {
            if (message.isError) {
                console.error(sender, name);
            } else {
                console.log(sender, name);
            }
            return;
        }

        if (name === STATUS_MESSAGES.TOS_REJECTED) {
            this._stopDisconnectionTimer();
            this._tosRejected = true;
        }

        //@ts-ignore
        if (description.endsMeeting) {
            this._stopDisconnectionTimer();
            this._meetingIsEndedByClient = true;
        }

        if (description.repetition === LOG_REPETITION_TYPE.SINGLE && this._registry[name] !== undefined) {
            // This message already presented
            return;
        }

        if (this._registry[name] === undefined) {
            this._registry[name] = {};
        }

        if (description.repetition === LOG_REPETITION_TYPE.UNIQUE && this._registry[name][message.id] !== undefined) {
            // Message with the same id already presented
            return;
        }

        this._registry[name][message.id] = true;

        this._prepareMessage({
            ...description,
            time: message.time,
            text: description.message,
            isNew
        });

        this.updateLogs();
    }

    private _stopDisconnectionTimer(): void {
        if (this._disconnectionTimer !== null) {
            window.clearTimeout(this._disconnectionTimer);
            this._disconnectionTimer = null;
        }
    }

    private _prepareMessage(messageData: MessageDescription): void {
        const msgTime = tsDate(messageData.time).isValid() ? tsDate(messageData.time) : tsDate();

        const preparedMessage: PreparedMessage = {
            order: ++this._messageCounter,
            group: messageData.group,
            text: messageData.text || messageData.message,
            accent: messageData.accent,
            time: msgTime.format('HH:mm:ss')
        };

        if (messageData.isNew) {
            this.emit('newEvent', preparedMessage);
        }

        this._messages.push(preparedMessage);
    }

    updateLogs(): void {
        const ordered = (cloneDeep(this._messages) || [])
            .map((message) => {
                message.index = ++this._updateCounter;
                return message;
            })
            .sort((a, b) => {
                if (a.time > b.time) return 1;
                if (a.time < b.time) return -1;
                if (a.group < 0 || b.group < 0) return a.order - b.order;
                if (a.group < b.group) return -1;
                if (a.group > b.group) return 1;
                return a.order - b.order;
            });

        this.emit('update', ordered);
    }

    mobileClientConnected(): void {
        this.connected = true;
        this._meetingIsEndedByClient = false;

        this._stopDisconnectionTimer();

        this.emit('logEvent', STATUS_MESSAGES.CLIENT_CONNECTED);
        this.emit('clientConnectionStatus', CONNECTION_STATUS.CONNECTED);
    }

    mobileClientDisconnected(): void {
        this._stopDisconnectionTimer();

        if (this._tosRejected) {
            return;
        }

        // Mobile client clicks end-meeting button.
        // So there is no reason to wait for reconnection
        if (this._meetingIsEndedByClient) {
            this.emit('clientConnectionStatus', CONNECTION_STATUS.DISCONNECTED);
            this.updateLogs();
            return;
        }

        if (RECONNECTION_SETTINGS.SHOW_RECONNECTION_STATUS) {
            this.emit('logEvent', STATUS_MESSAGES.CLIENT_RECONNECTING);
            this.emit('clientConnectionStatus', CONNECTION_STATUS.RECONNECTING);
        }

        if (RECONNECTION_SETTINGS.TIMEOUT <= 0) {
            this.connected = false;
            this.emit('logEvent', STATUS_MESSAGES.CLIENT_DISCONNECTED);
            getRootStore()?.eventLogService?.mobileDisconnected();
            this.emit('clientConnectionStatus', CONNECTION_STATUS.DISCONNECTED);
        } else {
            this._disconnectionTimer = window.setTimeout(() => {
                this._disconnectionTimer = null;
                this.connected = false;
                this.emit('logEvent', STATUS_MESSAGES.CLIENT_DISCONNECTED);
                getRootStore()?.eventLogService?.mobileDisconnected();
                this.emit('clientConnectionStatus', CONNECTION_STATUS.DISCONNECTED);
                this.updateLogs();
            }, RECONNECTION_SETTINGS.TIMEOUT * 1000);
        }

        this.updateLogs();
    }

    eventIsError(event: PreparedMessage): boolean {
        return event.accent === this.ACCENT_CLASS.DANGER;
    }

    eventIsWarning(event: PreparedMessage): boolean {
        return event.accent === this.ACCENT_CLASS.WARNING;
    }

    loadLocale(locale?: string, companyName?: string): void {
        let nextLocale = locale;

        if (!nextLocale) {
            nextLocale = this.getLocale();
        }

        this.browserUtilsService.saveToLocalStorage(DASHBOARD_LANGUAGE_KEY, nextLocale);
    }

    getLocale = (): string => {
        return this.browserUtilsService.getFromLocalStorage(DASHBOARD_LANGUAGE_KEY) || 'en_US';
    };

    isRTL(): boolean {
        return includes(RTL_LANGUAGES, this.getLocale());
    }

    clear(): void {
        this._messages = [];
        this._messageCounter = 0;
        this._updateCounter = 0;
        this._registry = {};
        this._disconnectionTimer = null;
        this._meetingIsEndedByClient = false;
        this._tosRejected = false;
    }

    // Additional methods (kept for completeness)
    mobileClientHidden(): void {
        if (this._tosRejected) return;
        this.emit('logEvent', STATUS_MESSAGES.CLIENT_HIDDEN);
    }

    mobileClientVisible(): void {
        this.emit('logEvent', STATUS_MESSAGES.CLIENT_VISIBLE);
    }

    mobileNetworkCell(): void {
        this.emit('logEvent', STATUS_MESSAGES.CLIENT_NETWORK_CELLULAR);
    }

    mobileNetworkWifi(): void {
        this.emit('logEvent', STATUS_MESSAGES.CLIENT_NETWORK_WIFI);
    }

    dashboardConnectedOffline(): void {
        this.emit('logEvent', STATUS_MESSAGES.ENTERED_OFFLINE_ROOM);
    }

    objectAnalyzingSuccess(): void {
        this.emit('logEvent', STATUS_MESSAGES.OBJECT_ANALYZING_SUCCESS);
    }

    objectAnalyzingError(): void {
        this.emit('logEvent', STATUS_MESSAGES.OBJECT_ANALYZING_ERROR);
    }

    objectAnalyzingStartTracking(): void {
        this.emit('logEvent', STATUS_MESSAGES.OBJECT_ANALYZING_START_TRACKING);
    }

    deviceAnalyzingError(): void {
        this.emit('logEvent', STATUS_MESSAGES.DEVICE_ANALYZING_ERROR);
    }
}
